import { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Link2, Loader2, Mail, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import {
  RULE_CATEGORIES,
  SEVERITIES,
  useCreateNotificationRoute,
  useCreateOrgDestination,
  useDeleteNotificationRoute,
  useDeleteOrgDestination,
  useNotificationRoutes,
  useOrgDestinations,
  useResendOrgDestinationVerification,
  useTestOrgDestination,
  useUpdateNotificationRoute,
  type DestinationKind,
  type NotificationDestination,
  type NotificationRoute,
  type RouteInput,
  type RuleSeverity,
} from '@/hooks/useNotifications.ts';
import { useAuth } from '@/hooks/useAuth.ts';
import { useConfirm } from '@/hooks/useConfirm.tsx';
import { useDivisions } from '@/hooks/useDivisions.ts';
import { useRegions } from '@/hooks/useRegions.ts';
import { usePermission } from '@/hooks/usePermission.ts';
import { useEntitlement, MODULES } from '@/hooks/useEntitlement.ts';
import { IS_TRIAL } from '@/lib/edition.ts';
import { SEVERITY_LABELS, routeSentence } from './ruleSentence.ts';
import { useClauseTargets, type ClauseTargets } from './useClauseTargets.ts';
import { ClauseList } from './ClauseEditor.tsx';
import { RoutingSimulator } from './RoutingSimulator.tsx';
import { MigrationReportList } from './MigrationReportList.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { Switch } from '@/components/ui/switch.tsx';

/**
 * Organization notification routing (§3.2, §5.2).
 *
 * Destinations and routes live in one section because neither is usable without the
 * other: a shared destination can only be selected by a route, and a route must name
 * a destination. Splitting them across two screens would mean every first-time setup
 * starts by navigating away from an empty list to build the thing it needs.
 *
 * Three gates apply, and they answer different questions (see `useEntitlement`):
 * **entitlement** — did this organization's plan buy org routing; **permission** —
 * may this person read or manage it; **edition** — can this build deliver to a kind
 * at all. None substitutes for another.
 */

/** Kinds a shared destination may be, and how to say them. */
const ORG_KINDS: { key: DestinationKind; label: string; saasOnly: boolean }[] = [
  { key: 'email', label: 'Email address', saasOnly: false },
  { key: 'webhook', label: 'Webhook (HTTP POST)', saasOnly: false },
  { key: 'slack_webhook', label: 'Slack incoming webhook', saasOnly: true },
  { key: 'teams_webhook', label: 'Microsoft Teams webhook', saasOnly: true },
];

const KIND_LABELS: Partial<Record<string, string>> = {
  email: 'Email',
  webhook: 'Webhook',
  slack_webhook: 'Slack',
  teams_webhook: 'Teams',
  web_push: 'Push',
  psa: 'PSA tickets',
};

function isWebhookKind(kind: string): boolean {
  return kind === 'webhook' || kind === 'slack_webhook' || kind === 'teams_webhook';
}

/**
 * A stored route as the composer's draft.
 *
 * Shared by the edit button and the enable switch because both write the *whole*
 * route — the API has no partial update — so a field missing from one of them would
 * be silently reset by whichever ran last. `destinationId` widens to `''` because a
 * route can lose its destination to a delete, and the picker needs a falsy value to
 * show its placeholder rather than an empty selection.
 */
function toRouteInput(route: NotificationRoute): RouteInput {
  return {
    name: route.name,
    divisionId: route.divisionId,
    destinationId: route.destinationId ?? '',
    categories: route.categories,
    categoriesNegated: route.categoriesNegated,
    minSeverity: route.minSeverity,
    maxSeverity: route.maxSeverity,
    includeResolutions: route.includeResolutions,
    enabled: route.enabled,
    clauses: route.clauses,
  };
}

/** Stable identity so the memoized target lists do not rebuild while a query loads. */
const EMPTY_LIST: { id: string; name: string }[] = [];

function isRateLimited(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}

/**
 * The backend's 400s are already written for a human; anything else is ours to phrase.
 *
 * 403 gets its own sentence because it is reachable by an honest mistake rather than a
 * bad one. `create_org` authorizes against the division the row will **belong to**, not
 * the caller's own — otherwise a division admin could mint a destination owned by
 * another tenant. The picker cannot pre-filter to the divisions this person manages,
 * because `usePermission` answers "do you hold it" and not "where", so the refusal has
 * to explain itself here.
 */
function requestErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;
  if (error.response?.status === 403) {
    return "You can't create this for that division — pick one you administer, or the whole organization.";
  }
  if (error.response?.status === 400) {
    const detail: unknown = error.response.data;
    if (typeof detail === 'string' && detail.length > 0) return detail;
    if (detail && typeof detail === 'object' && 'error' in detail && typeof detail.error === 'string') {
      return detail.error;
    }
  }
  return fallback;
}

function DestinationRow({
  destination,
  divisionName,
  canManage,
}: {
  destination: NotificationDestination;
  divisionName: string | null;
  canManage: boolean;
}) {
  const test = useTestOrgDestination();
  const resend = useResendOrgDestinationVerification();
  const remove = useDeleteOrgDestination();
  const { confirm, confirmDialog } = useConfirm();

  const webhook = isWebhookKind(destination.kind);

  const onRemove = async () => {
    const ok = await confirm({
      title: 'Remove this destination?',
      message: `${destination.target} will stop receiving notifications, and any route pointing at it will stop delivering.`,
      confirmText: 'Remove',
      isDanger: true,
    });
    if (ok) remove.mutate(destination.id);
  };

  return (
    <div className="rounded-md border border-border-subtle px-3 py-2 space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm min-w-0">
          {destination.kind === 'email' ? (
            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          ) : (
            <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="truncate">{destination.target}</span>
          <Badge className="bg-muted text-muted-foreground border-0 font-normal">
            {KIND_LABELS[destination.kind] ?? destination.kind}
          </Badge>
          {divisionName ? (
            <Badge className="bg-muted text-muted-foreground border-0 font-normal">{divisionName}</Badge>
          ) : null}
          {destination.verified ? null : (
            <Badge className="bg-sev-2/15 text-sev-2 border-0 font-normal">Unverified</Badge>
          )}
        </span>
        {canManage && destination.kind === 'psa' ? (
          // A PSA connection's destination is made and removed with the connection.
          <span className="text-xs text-muted-foreground shrink-0">Managed under PSA ticketing</span>
        ) : canManage ? (
          <span className="flex items-center gap-1.5 shrink-0">
            {/* Offered on an unverified webhook too, unlike the personal email list —
                here the test IS the verification (a 2xx through the SSRF guard, §5.2),
                so it is the action that moves the row forward rather than one that can
                only be refused. */}
            {destination.verified || webhook ? (
              <Button size="sm" variant="outline" onClick={() => { test.mutate(destination.id); }} disabled={test.isPending}>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {test.isPending ? 'Sending…' : webhook && !destination.verified ? 'Test & verify' : 'Send test'}
              </Button>
            ) : null}
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => { void onRemove(); }}
              disabled={remove.isPending}
              aria-label={`Remove ${destination.target}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </span>
        ) : null}
      </div>

      {!destination.verified && destination.kind === 'email' ? (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            Waiting for confirmation — someone with access to {destination.target} has to open the link we
            emailed. Owning the address as an organization is not evidence the mailbox agreed to receive.
          </p>
          {canManage ? (
            <>
              <Button size="sm" variant="outline" onClick={() => { resend.mutate(destination.id); }} disabled={resend.isPending}>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {resend.isPending ? 'Sending…' : 'Resend confirmation'}
              </Button>
              {resend.isSuccess ? (
                <p className="text-xs text-muted-foreground">Sent. The newest link is the one that works.</p>
              ) : null}
              {resend.isError ? (
                <p className="text-xs text-destructive">
                  {isRateLimited(resend.error) ? 'Just sent one — give it a minute.' : "Couldn't resend the confirmation."}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      {!destination.verified && webhook ? (
        <p className="text-xs text-muted-foreground">
          Send a test to verify it. A 2xx answer proves the URL exists and accepts our shape — that is the
          verification for a webhook.
        </p>
      ) : null}

      {destination.verified && destination.failureCount > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {destination.failureCount === 1
            ? 'The last delivery here failed.'
            : `The last ${String(destination.failureCount)} deliveries here failed.`}
        </p>
      ) : null}

      {test.isSuccess && !test.data.delivered ? (
        <p className="text-xs text-destructive">{test.data.detail ?? "The test couldn't be delivered."}</p>
      ) : null}
      {test.isSuccess && test.data.delivered ? (
        <p className="text-xs text-muted-foreground">
          {test.data.verified ? 'Delivered — this destination is verified.' : 'Delivered.'}
        </p>
      ) : null}
      {test.isError ? (
        <p className="text-xs text-destructive">
          {isRateLimited(test.error) ? 'Just tested — give it a minute before trying again.' : "Couldn't send a test."}
        </p>
      ) : null}

      {confirmDialog}
    </div>
  );
}

function AddDestination({ divisions }: { divisions: { id: string; name: string }[] }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<DestinationKind>('email');
  const [value, setValue] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [divisionId, setDivisionId] = useState<string>('org');
  const create = useCreateOrgDestination();

  // The trial cannot deliver to Slack or Teams (`kind_available` is `cfg!(saas)`), and
  // the backend refuses the kind at create. Offering it anyway would be a control whose
  // only outcome is a 400.
  const kinds = ORG_KINDS.filter((k) => !k.saasOnly || !IS_TRIAL);
  const isEmail = kind === 'email';

  const reset = () => {
    setOpen(false);
    setKind('email');
    setValue('');
    setDisplayName('');
    setDivisionId('org');
  };

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    create.mutate(
      {
        kind,
        displayName: displayName.trim() || undefined,
        address: isEmail ? trimmed : undefined,
        url: isEmail ? undefined : trimmed,
        divisionId: divisionId === 'org' ? null : divisionId,
      },
      { onSuccess: reset },
    );
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => { setOpen(true); }}>
        <Plus className="h-3.5 w-3.5 mr-1.5" /> Add destination
      </Button>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Kind</Label>
          <Select value={kind} onValueChange={(v) => { setKind(v as DestinationKind); setValue(''); }}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {kinds.map((k) => (
                <SelectItem key={k.key} value={k.key}>
                  {k.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Owned by</Label>
          <Select value={divisionId} onValueChange={setDivisionId}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="org">The whole organization</SelectItem>
              {divisions.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">{isEmail ? 'Address' : 'URL'}</Label>
        <Input
          value={value}
          type={isEmail ? 'email' : 'url'}
          placeholder={isEmail ? 'noc@example.com' : 'https://hooks.example.com/services/…'}
          className="h-8 text-sm"
          onChange={(e) => { setValue(e.target.value); }}
          aria-label={isEmail ? 'Destination email address' : 'Destination URL'}
        />
        {isEmail ? null : (
          <p className="text-xs text-muted-foreground">
            Stored write-only — a webhook URL is a credential, so it is never shown again once saved.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Name (optional)</Label>
        <Input
          value={displayName}
          placeholder="NOC channel"
          className="h-8 text-sm"
          onChange={(e) => { setDisplayName(e.target.value); }}
          aria-label="Destination name"
        />
      </div>

      {create.isError ? (
        <p className="text-xs text-destructive">{requestErrorMessage(create.error, "Couldn't add that destination.")}</p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={create.isPending || !value.trim()}>
          {create.isPending ? 'Adding…' : 'Add destination'}
        </Button>
        <Button size="sm" variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const EMPTY_ROUTE: RouteInput = {
  name: '',
  divisionId: null,
  destinationId: '',
  categories: null,
  categoriesNegated: false,
  minSeverity: 'high',
  maxSeverity: null,
  includeResolutions: true,
  enabled: true,
  clauses: [],
};

/** The route composer. Previews the sentence live, exactly as the rule composer does. */
function RouteComposer({
  initial,
  targets,
  organizationId,
  divisions,
  destinations,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  initial: RouteInput;
  targets: ClauseTargets;
  organizationId: string | undefined;
  divisions: { id: string; name: string }[];
  destinations: NotificationDestination[];
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onSubmit: (route: RouteInput) => void;
}) {
  const [draft, setDraft] = useState<RouteInput>(initial);
  const { scopes, tagDefinitions, tagValueLabels } = targets;

  const chosen = destinations.find((d) => d.id === draft.destinationId);
  const preview = routeSentence(
    {
      ...draft,
      id: '',
      destinationName: chosen?.displayName ?? chosen?.target ?? null,
      destinationKind: chosen?.kind ?? null,
      alertDefinitionId: null,
      createdAt: '',
      updatedAt: '',
    } satisfies NotificationRoute,
    scopes,
    tagValueLabels,
  );

  const toggleCategory = (key: string) => {
    setDraft((d) => {
      // `null` means "every category, including ones that do not exist yet" — the
      // storage keeps that meaning, and the checkboxes show it as all-ticked. So the
      // first uncheck has to materialise the full list before removing from it,
      // otherwise it would read as unchecking one box and get stored as checking one.
      const current = d.categories ?? RULE_CATEGORIES.map((c) => c.key);
      const next = current.includes(key) ? current.filter((c) => c !== key) : [...current, key];
      // Back to all-ticked collapses to `null` rather than freezing today's list. An
      // explicit list of every category is NOT the same claim: add a ninth category and
      // a frozen list silently stops covering it, which is how a firehose rule quietly
      // develops a blind spot.
      return { ...d, categories: next.length === RULE_CATEGORIES.length ? null : next };
    });
  };

  // An unverified destination resolves to `skipped_unverified` on every event, so a
  // route naming one is configuration that looks live and delivers nothing.
  const selectable = destinations.filter((d) => d.verified);

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
      <p className="text-sm font-medium">{preview}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-xs">Name</Label>
          <Input
            value={draft.name}
            placeholder="Page the NOC"
            className="h-8 text-sm"
            onChange={(e) => { setDraft((d) => ({ ...d, name: e.target.value })); }}
            aria-label="Route name"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Send to</Label>
          <Select
            value={draft.destinationId}
            onValueChange={(v) => { setDraft((d) => ({ ...d, destinationId: v })); }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Choose a destination" />
            </SelectTrigger>
            <SelectContent>
              {selectable.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.displayName || d.target} — {KIND_LABELS[d.kind] ?? d.kind}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectable.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No verified destinations yet. Add and verify one on the Shared destinations page first.
            </p>
          ) : null}
          {selectable.find((d) => d.id === draft.destinationId)?.kind === 'psa' ? (
            <p className="text-xs text-muted-foreground">
              Each matching incident gets a ticket once the connection’s settle delay passes, unless it
              recovers or rolls up under a broader incident first. Tickets are made from incidents only.
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Administered by</Label>
          <Select
            value={draft.divisionId ?? 'org'}
            onValueChange={(v) => { setDraft((d) => ({ ...d, divisionId: v === 'org' ? null : v })); }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="org">The whole organization</SelectItem>
              {divisions.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* §3.2's central distinction: ownership is who administers the route,
              narrowing is a clause. Collapsing them would let a division admin write a
              route that reaches beyond their division. */}
          <p className="text-xs text-muted-foreground">Who can edit this route — not what it matches.</p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Severity</Label>
          <div className="flex items-center gap-2">
            <Select
              value={draft.minSeverity}
              onValueChange={(v) => { setDraft((d) => ({ ...d, minSeverity: v as RuleSeverity })); }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SEVERITY_LABELS[s]} and above
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">About</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {RULE_CATEGORIES.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-xs" title={c.description}>
              <Checkbox
                checked={draft.categories?.includes(c.key) ?? true}
                // The last remaining category cannot be unticked. Allowing it would
                // need a "no categories" state, and the only two candidate meanings
                // are "match nothing" (a rule that exists to do nothing) and "match
                // everything" (the inversion this change removed). Refusing the click
                // is the honest third option.
                disabled={draft.categories?.length === 1 && draft.categories[0] === c.key}
                onCheckedChange={() => { toggleCategory(c.key); }}
              />
              {c.label}
            </label>
          ))}
        </div>
        {/* No hint about "empty means everything" any more, because it no longer does.
            All-ticked is the default and it is shown as all-ticked; the last box cannot
            be unticked, because a rule about no categories is a rule about nothing. */}
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Where</Label>
        <ClauseList
          clauses={draft.clauses}
          scopes={scopes}
          organizationId={organizationId}
          tagDefinitions={tagDefinitions}
          tagValueLabels={tagValueLabels}
          onChange={(next) => { setDraft((d) => ({ ...d, clauses: next })); }}
        />
        <p className="text-xs text-muted-foreground">
          No conditions means the whole organization. Conditions combine with “and” — for “or”, add a second route.
        </p>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <Switch
          checked={draft.includeResolutions}
          onCheckedChange={(v) => { setDraft((d) => ({ ...d, includeResolutions: v })); }}
        />
        Send resolutions here, too
      </label>

      {/* A save can fail for a reason only the server knows — a division this person
          does not administer, a destination deleted since the list loaded. Without this
          the composer just sits there looking idle. */}
      {error ? (
        <p className="text-xs text-destructive">{requestErrorMessage(error, "Couldn't save that route.")}</p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => { onSubmit(draft); }} disabled={busy || !draft.name.trim() || !draft.destinationId}>
          {busy ? 'Saving…' : 'Save route'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Which half to render.
 *
 * The two halves stayed one component when they were one section, and they stay one
 * component now that they are two pages: the route composer's destination picker
 * reads the same destinations list the other half manages, and the three gates
 * (entitlement, permission, edition) have to agree across both. Splitting the file
 * would mean two copies of that gating, which is how one of them ends up wrong.
 */
export type OrgRoutingHalf = 'destinations' | 'routes' | 'all';

export function OrgNotificationRouting({ show = 'all' }: { show?: OrgRoutingHalf } = {}) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? undefined;
  const entitled = useEntitlement(MODULES.notificationRoutes);
  const canRead = usePermission('notification_route:read');
  const canManage = usePermission('notification_route:manage');

  const enabled = entitled && canRead;
  const { data: destinations, isLoading: destinationsLoading } = useOrgDestinations(enabled);
  const { data: routes, isLoading: routesLoading } = useNotificationRoutes(enabled);
  const { data: divisions } = useDivisions(organizationId);
  const { data: regions } = useRegions(organizationId);

  const createRoute = useCreateNotificationRoute();
  const updateRoute = useUpdateNotificationRoute();
  const deleteRoute = useDeleteNotificationRoute();
  const { confirm, confirmDialog } = useConfirm();

  const [composing, setComposing] = useState<{ id: string | null; draft: RouteInput } | null>(null);

  // Every clause on screen, including the one being edited, so a pinned tag value can
  // be named in the list and in the live preview alike.
  const targets = useClauseTargets(
    organizationId,
    divisions ?? EMPTY_LIST,
    regions ?? EMPTY_LIST,
    [...(routes ?? []).flatMap((r) => r.clauses), ...(composing?.draft.clauses ?? [])],
  );
  const { scopes, tagValueLabels } = targets;

  // Entitlement and permission are separate questions and deserve separate answers,
  // but neither is actionable from here — so the section simply is not there, rather
  // than being there and refusing.
  if (!enabled) return null;

  const divisionList = (divisions ?? []).map((d) => ({ id: d.id, name: d.name }));
  const divisionName = (id: string | null) => divisionList.find((d) => d.id === id)?.name ?? null;

  const onDeleteRoute = async (route: NotificationRoute) => {
    const ok = await confirm({
      title: `Delete “${route.name}”?`,
      message: 'This organization will stop sending those notifications to that destination.',
      confirmText: 'Delete',
      isDanger: true,
    });
    if (ok) deleteRoute.mutate(route.id);
  };

  // Clearing both on every open and close: a failed create otherwise shows its error
  // in the next composer, and `??` below would let a stale create error mask the real
  // one while editing.
  const closeComposer = () => {
    createRoute.reset();
    updateRoute.reset();
    setComposing(null);
  };

  const submitRoute = (draft: RouteInput) => {
    const done = { onSuccess: closeComposer };
    if (composing?.id) updateRoute.mutate({ id: composing.id, route: draft }, done);
    else createRoute.mutate(draft, done);
  };

  return (
    <section className="bg-card border border-border rounded-lg p-5 space-y-5">
      {/* Only when this renders as a section inside a larger page. Split across two
          pages, each supplies its own PageHeader and a second heading here would be
          the same sentence twice. */}
      {show === 'all' ? (
        <div>
          <h2 className="text-sm font-semibold">Notification routing</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Where this organization&apos;s notifications go, independently of anyone&apos;s personal rules. People still
            get their own notifications; these reach a shared address whether or not anyone subscribed.
          </p>
        </div>
      ) : null}

      {/* Sits with the routes rather than the destinations: an unreviewed row means
          somebody may not be getting paged right now, which is a routing question. */}
      {show === 'destinations' ? null : <MigrationReportList enabled={enabled} />}

      {show === 'routes' ? null : (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Shared destinations</h3>
        {destinationsLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {(destinations ?? []).map((d) => (
              <DestinationRow
                key={d.id}
                destination={d}
                divisionName={divisionName(d.divisionId)}
                canManage={canManage}
              />
            ))}
            {(destinations ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Nothing shared yet. A destination here is reachable by routes, not by personal rules.
              </p>
            ) : null}
            {canManage ? <AddDestination divisions={divisionList} /> : null}
          </>
        )}
      </div>
      )}

      {show === 'destinations' ? null : (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Routes</h3>
        {routesLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {(routes ?? []).map((route) => (
              <div key={route.id} className="rounded-md border border-border-subtle px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      {route.name}
                      {/* The switch carries this state for anyone who can flip it. A
                          reader who cannot gets the badge instead, so "why is the NOC
                          quiet" is answerable without permission to fix it. */}
                      {route.enabled || canManage ? null : (
                        <Badge className="bg-muted text-muted-foreground border-0 font-normal">Disabled</Badge>
                      )}
                      {divisionName(route.divisionId) ? (
                        <Badge className="bg-muted text-muted-foreground border-0 font-normal">
                          {divisionName(route.divisionId)}
                        </Badge>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted-foreground">{routeSentence(route, scopes, tagValueLabels)}</p>
                  </div>
                  {canManage ? (
                    <span className="flex items-center gap-1.5 shrink-0">
                      {/* Silencing a route during a maintenance window used to mean
                          deleting it and building it again from memory. It is a state,
                          not an edit, so it lives on the row rather than behind the
                          composer — and the composer keeps writing the same field, so
                          neither place can drift from the other. */}
                      <Switch
                        checked={route.enabled}
                        aria-label={route.enabled ? `Turn off ${route.name}` : `Turn on ${route.name}`}
                        disabled={updateRoute.isPending && updateRoute.variables.id === route.id}
                        onCheckedChange={(enabled) => {
                          updateRoute.mutate({ id: route.id, route: { ...toRouteInput(route), enabled } });
                        }}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        aria-label={`Edit ${route.name}`}
                        onClick={() => {
                          createRoute.reset();
                          updateRoute.reset();
                          setComposing({ id: route.id, draft: toRouteInput(route) });
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        aria-label={`Delete ${route.name}`}
                        onClick={() => { void onDeleteRoute(route); }}
                        disabled={deleteRoute.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  ) : null}
                </div>
              </div>
            ))}

            {(routes ?? []).length === 0 && !composing ? (
              <p className="text-xs text-muted-foreground">
                No routes yet. Without one, an incident reaches only the people whose own rules match it.
              </p>
            ) : null}

            {composing ? (
              <RouteComposer
                // Keyed, and load-bearing. The route list above stays rendered while
                // composing, so its edit buttons are live: switching from one route to
                // another swaps `composing` without changing this element's type or
                // position, React reuses the instance, and `useState(initial)` — which
                // only runs on mount — keeps the FIRST route's draft. Saving then writes
                // it over the second route. `NotificationRules` is immune only because
                // its composer sits inside `<li key={rule.id}>`.
                key={composing.id ?? 'new'}
                initial={composing.draft}
                targets={targets}
                organizationId={organizationId}
                divisions={divisionList}
                destinations={destinations ?? []}
                busy={createRoute.isPending || updateRoute.isPending}
                error={createRoute.error ?? updateRoute.error}
                onCancel={closeComposer}
                onSubmit={submitRoute}
              />
            ) : canManage ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  createRoute.reset();
                  updateRoute.reset();
                  setComposing({ id: null, draft: EMPTY_ROUTE });
                }}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Add route
              </Button>
            ) : null}

            {/* Under the routes it explains, and offered to readers as well as
                managers: "would this page anyone?" is a question an on-call engineer
                asks about a policy they cannot edit. */}
            {composing ? null : <RoutingSimulator scopes={scopes} />}
          </>
        )}
      </div>
      )}

      {confirmDialog}
    </section>
  );
}
